import type { ExperienceCategory, ExperiencePlace, PassportStamp, PassportVisit, PassportVisitEvidence } from './models';

export const PASSPORT_STICKERS = [
  { id: 'price-detective', label: '가격 탐정', mark: '₩' },
  { id: 'careful-planner', label: '꼼꼼한 계획가', mark: 'P' },
  { id: 'fact-checker', label: '정보 확인가', mark: 'i' },
  { id: 'family-reporter', label: '가족 기록가', mark: 'F' },
] as const;

export type PassportStickerId = (typeof PASSPORT_STICKERS)[number]['id'];

export const PASSPORT_MILESTONES = [
  { count: 4, title: '동네 경제 탐험가', copy: '서로 다른 생활 경제 단서를 네 개 발견해요.' },
  { count: 8, title: 'MRM 금융 패스포트 완주자', copy: '월드의 핵심 탐험 스탬프 여덟 개를 모아요.' },
  { count: 12, title: '현실 경제 연결자', copy: '앱의 배움을 가족의 실제 행동으로 확장해요.' },
] as const;

const categoryOrder: ExperienceCategory[] = ['market','banking','money','business','tax','sharing','history','investment'];

export function publishedExperiencePlaces(places: ExperiencePlace[]): ExperiencePlace[] {
  return places.filter((place) => place.status === 'published');
}

export function passportProgress(visits: PassportVisit[], stamps: PassportStamp[], childId: string) {
  const childVisits = visits.filter((visit) => visit.childId === childId);
  return {
    approved: childVisits.filter((visit) => visit.status === 'approved').length,
    pending: childVisits.filter((visit) => visit.status === 'submitted').length,
    held: childVisits.filter((visit) => visit.status === 'held').length,
    stamps: stamps.filter((stamp) => stamp.childId === childId).length
  };
}

export function passportMilestoneState(stampCount: number) {
  const next = PASSPORT_MILESTONES.find((item) => stampCount < item.count) ?? PASSPORT_MILESTONES[PASSPORT_MILESTONES.length - 1];
  const previous = [...PASSPORT_MILESTONES].reverse().find((item) => stampCount >= item.count);
  const base = previous?.count ?? 0;
  const span = Math.max(1, next.count - base);
  const percent = stampCount >= PASSPORT_MILESTONES[PASSPORT_MILESTONES.length - 1].count ? 100 : Math.min(100, Math.round(((stampCount - base) / span) * 100));
  return { next, previous, percent, remaining: Math.max(0, next.count - stampCount) };
}

export function passportCategoryProgress(places: ExperiencePlace[], visits: PassportVisit[], childId: string) {
  const approved = new Set(visits.filter((visit) => visit.childId === childId && visit.status === 'approved').map((visit) => visit.placeId));
  return categoryOrder.map((category) => {
    const categoryPlaces = places.filter((place) => place.category === category);
    return {
      category,
      total: categoryPlaces.length,
      completed: categoryPlaces.filter((place) => approved.has(place.id)).length,
    };
  }).filter((item) => item.total > 0);
}

export function nextPassportPlace(places: ExperiencePlace[], visits: PassportVisit[], childId: string): ExperiencePlace | undefined {
  const childVisits = visits.filter((visit) => visit.childId === childId);
  const activeIds = new Set(childVisits.filter((visit) => visit.status === 'submitted').map((visit) => visit.placeId));
  const approvedIds = new Set(childVisits.filter((visit) => visit.status === 'approved').map((visit) => visit.placeId));
  const published = publishedExperiencePlaces(places);
  return published.find((place) => !approvedIds.has(place.id) && !activeIds.has(place.id)) ?? published.find((place) => !approvedIds.has(place.id)) ?? published[0];
}

export function passportPrivacyIssue(observation: string): string | null {
  const value = observation.trim();
  if (/\b\d{12,19}\b/.test(value.replace(/[ -]/g,''))) return '카드번호·계좌번호처럼 긴 숫자는 기록하지 않아요.';
  if (/(우리\s*집\s*주소|집\s*주소|학교\s*이름|학교명|카드\s*번호|계좌\s*번호|비밀번호)/i.test(value)) return '주소·학교명·카드번호·계좌번호·비밀번호는 기록하지 않아요.';
  return null;
}

export function validatePassportEvidence(place: ExperiencePlace, method: PassportVisit['verificationMethod'], evidence: PassportVisitEvidence): string | null {
  if (!place.verificationMethods.includes(method)) return '이 장소에서 지원하지 않는 인증 방식입니다.';
  if (evidence.observation.trim().length < 10) return '관찰 기록을 10자 이상 작성해 주세요.';
  if (evidence.observation.trim().length > 500) return '관찰 기록은 500자 이하로 작성해 주세요.';
  const privacyIssue = passportPrivacyIssue(evidence.observation);
  if (privacyIssue) return privacyIssue;
  if (method === 'code' && evidence.verificationCode?.trim().toUpperCase() !== place.verificationCode?.trim().toUpperCase()) return '현장 인증 코드가 맞지 않습니다.';
  // Release 2.7: photo-note means “observation note, photo optional”.
  if (method === 'gps' && !evidence.locationConfirmed) return '위치 확인이 필요합니다.';
  if (method === 'nfc' && !evidence.nfcConfirmed) return 'NFC 인증이 필요합니다.';
  return null;
}

export function submitPassportVisit(visit: PassportVisit, place: ExperiencePlace): PassportVisit {
  const issue = validatePassportEvidence(place, visit.verificationMethod, visit.evidence);
  if (issue) throw new Error('VALIDATION_ERROR');
  const now = new Date().toISOString();
  return {
    ...visit,
    status: 'submitted',
    evidence: {
      ...visit.evidence,
      photoSkipped: !visit.evidence.photoFileName,
      guardianConfirmed: false,
    },
    submittedAt: now,
    updatedAt: now,
  };
}

export function reviewPassportVisit(visit: PassportVisit, decision: 'approved' | 'held' | 'rejected', parentNote: string): PassportVisit {
  if (visit.status !== 'submitted') throw new Error('VISIT_STATE_CONFLICT');
  const now = new Date().toISOString();
  return {
    ...visit,
    status: decision,
    evidence: { ...visit.evidence, guardianConfirmed: decision === 'approved' },
    parentNote: parentNote.trim(),
    reviewedAt: now,
    updatedAt: now,
  };
}

export function stampForVisit(visit: PassportVisit, place: ExperiencePlace): PassportStamp {
  if (visit.status !== 'approved') throw new Error('VISIT_STATE_CONFLICT');
  const mark: Record<ExperienceCategory, string> = { money:'₩', banking:'B', tax:'T', market:'M', business:'C', investment:'I', history:'H', sharing:'S' };
  return {
    id: `stamp_${visit.id}`,
    childId: visit.childId,
    placeId: visit.placeId,
    title: `${place.name} 탐험 스탬프`,
    icon: mark[place.category],
    year: new Date(visit.visitedAt).getFullYear(),
    earnedAt: visit.reviewedAt ?? new Date().toISOString()
  };
}
